import { DateTime } from "luxon";
import type { Student } from "../providers/studentsProvider";
import type { DrivingLicenseClass } from "./DrivingLicenseClass";

interface AgeRequirement {
  years: number;
  months: number;
}

interface LicenseClassAgeRules {
  theory: AgeRequirement;
  /** `null` means no practical exam is needed for this class (e.g. class M). */
  practical: AgeRequirement | null;
}

/**
 * Minimum age at which a student is allowed to take the theory / practical exam for a given
 * driving license class.
 * Returns `null` for classes without a defined age rule.
 */
function getLicenseClassAgeRules(
  drivingLicenseClass: DrivingLicenseClass,
  { bf17 }: { bf17: boolean },
): LicenseClassAgeRules | null {
  switch (drivingLicenseClass) {
    case "B":
    case "B197":
    case "B78":
    case "BE":
      return bf17
        ? { theory: { years: 16, months: 9 }, practical: { years: 16, months: 11 } }
        : { theory: { years: 17, months: 9 }, practical: { years: 17, months: 11 } };
    case "A2":
      return { theory: { years: 17, months: 9 }, practical: { years: 17, months: 11 } };
    case "AM":
      return { theory: { years: 14, months: 9 }, practical: { years: 14, months: 11 } };
    case "A1":
      return { theory: { years: 15, months: 9 }, practical: { years: 15, months: 11 } };
    case "M":
      return { theory: { years: 14, months: 9 }, practical: null };
    case "A":
    case "A80":
      return { theory: { years: 23, months: 9 }, practical: { years: 23, months: 11 } };
    default:
      return null;
  }
}

function getEligibilityDate(student: Student, requirement: AgeRequirement): DateTime | null {
  if (!student.dateOfBirth) {
    return null;
  }
  return student.dateOfBirth.toDateTime().plus({ years: requirement.years, months: requirement.months }).startOf("day");
}

function lastTakenTheoryExam(student: Student): { date: DateTime; result: "passed" | "failed" } | undefined {
  return Object.values(student.kpis.theoryExams)
    .filter((exam): exam is { date: DateTime; result: "passed" | "failed" } => !!exam.result)
    .reduce<{ date: DateTime; result: "passed" | "failed" } | undefined>(
      (latest, exam) => (!latest || exam.date.toMillis() > latest.date.toMillis() ? exam : latest),
      undefined,
    );
}

export function getTheoryExamEligibilityDate(
  student: Student,
  drivingLicenseClass: DrivingLicenseClass,
): DateTime | null {
  const lastTheoryExam = lastTakenTheoryExam(student);
  if (lastTheoryExam) {
    if (lastTheoryExam.result === "failed") {
      return lastTheoryExam.date.plus({ weeks: 2 }).startOf("day");
    } else {
      return null;
    }
  }

  const classAgeRules = getLicenseClassAgeRules(drivingLicenseClass, { bf17: student.bf17 });
  if (!classAgeRules) {
    return null;
  }
  const eligibilityDate = getEligibilityDate(student, classAgeRules.theory);
  if (!eligibilityDate) {
    return null;
  }
  return eligibilityDate;
}

export function getPracticalExamEligibilityDate(
  student: Student,
  drivingLicenseClass: DrivingLicenseClass,
): DateTime | null {
  const classAgeRules = getLicenseClassAgeRules(drivingLicenseClass, { bf17: student.bf17 });
  if (!classAgeRules || !classAgeRules.practical) {
    return null;
  }
  const eligibilityDate = getEligibilityDate(student, classAgeRules.practical);
  if (!eligibilityDate) {
    return null;
  }
  return eligibilityDate;
}
